import { MdClose } from 'react-icons/md';
import { useState, useEffect } from 'react';
import Modal from 'components/shared/modal';
import { useFormik } from 'formik';
import * as yup from 'yup';
import InputField from 'components/shared/input-field';
import { appAxios } from 'api/axios';
import { getTokenDetails } from 'functions/userSession';
import { decodeToken } from 'react-jwt';
import { TokenType } from 'types/jwt';
import { Loader } from 'lucide-react';
import { formatToNaira } from 'functions/currencyConveter';
import { sendCatchFeedback, sendFeedback } from 'functions/feedback';
import AuthAnimatedModal from 'components/shared/modal/AuthAnimatedModal';
import { toast } from 'react-toastify';
import SelectInput from 'components/shared/select-input';
import { OptionType } from '../../../../types';
import CardDisclaimerModal from 'components/shared/modal/CardDisclaimerModal';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  goalId: string;
};

const validationSchema = yup.object({
  amount: yup.number().required('Amount is required'),
  savingSource: yup.string().required('Savings source is required'),
});

function AddSavingsModal({ isOpen, onClose, goalId }: Props) {
  const token = getTokenDetails();
  const decodedToken = decodeToken<TokenType>(token!);
  const customerId = decodedToken?.customerId;
  const [loading, setLoading] = useState(false);
  const [isModalSuccessOpen, setIsModalSuccessOpen] = useState(false);
  const [formattedAmount, setFormattedAmount] = useState('');
  const [sourceOptions, setSourceOptions] = useState<OptionType[]>([]);
  const [loadingSourceOptions, setLoadingSourceOptions] = useState(false);

  // Fetch saving sources
  useEffect(() => {
    const fetchSourceOptions = async () => {
      if (isOpen) {
        setLoadingSourceOptions(true);
        try {
          const { data } = await appAxios.get('/get-saving-source');
          if (data && data.statusCode === 200) {
            const formattedOptions = data.data.map((source: any) => ({
              label: source.name,
              value: source.id,
            }));
            setSourceOptions(formattedOptions);
          }
        } catch (error) {
          console.error('Error fetching saving sources:', error);
          toast.error('Failed to load saving sources');
        } finally {
          setLoadingSourceOptions(false);
        }
      }
    };

    fetchSourceOptions();
  }, [isOpen]);

  const formik = useFormik({
    initialValues: {
      customerId: customerId,
      amount: '',
      savingsGoalId: goalId,
      savingSource: '',
      cardToken: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      console.log('Form submitted with values:', values);
      setLoading(true);
      try {
        const { customerId, amount, savingsGoalId, savingSource, cardToken } = values;
        console.log('Making API call with:', {
          customerId,
          amount,
          savingsGoalId,
          savingSource,
        });
        const { data } = formik.values.savingSource === 'Wallet' ? await appAxios.post(
          `/add-contribution?goalId=${savingsGoalId}&amount=${amount}&savingSource=${savingSource}`
        ) : await appAxios.post(
          `/credit-from-card?goalId=${savingsGoalId}&amount=${amount}&savingSource=${savingSource}` + 
          (cardToken ? `&cardToken=${cardToken}` : '')
        );
        toast(data?.message, { type: 'success' });
        setIsModalSuccessOpen(true);
        onClose();
      } catch (error) {
        console.error('API error:', error);
        sendCatchFeedback(error);
        onClose();
      } finally {
        setLoading(false);
      }
    },
  });

  const handleSourceSelection = (name: string, option: OptionType | null) => {
    if (option) {
      formik.setFieldValue('savingSource', option.label);
    }
  };


  const handleProceed = () => {
    setIsModalSuccessOpen(false);
    onClose();
    window.location.reload();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Savings from Wallet/Card"
      subtitle="Please enter the amount you want to add to your savings"
      width="400px"
    >
      <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
        <InputField
          name="amount"
          label="Amount to add"
          type="text"
          placeholder="Enter amount to add"
          value={formattedAmount}
          onChange={(e) => {
            const value = (e.target as HTMLInputElement).value;
            // Remove all non-digit characters
            const numericValue = value.replace(/\D/g, '');
            
            if (numericValue === '') {
              setFormattedAmount('');
              formik.setFieldValue('amount', '');
            } else {
              // Format with commas for display
              const formattedWithCommas = Number(numericValue).toLocaleString();
              setFormattedAmount(formattedWithCommas);
              // Set the actual numeric value (without commas) in formik
              formik.setFieldValue('amount', numericValue);
            }
          }}
          onBlur={formik.handleBlur}
          error={formik.touched.amount ? formik.errors.amount : ''}
        />

        <SelectInput
          label="Saving Source"
          name="savingSource"
          placeholder={loadingSourceOptions ? "Loading saving sources..." : "Select saving source"}
          options={sourceOptions}
          value={sourceOptions.find(option => option.label === formik.values.savingSource) || null}
          onChange={handleSourceSelection}
          required
          error={formik.touched.savingSource ? formik.errors.savingSource : ''}
        />

        <div className="flex gap-x-4 mt-4 mb-6">
          <button
            type="button"
            className="flex-1 border border-[#EFA005] outline-none rounded-lg text-[#EFA005] p-1"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 border border-[#EFA005] outline-none rounded-lg bg-[#EFA005] text-white p-1"
            disabled={loading || loadingSourceOptions}
          >
            {loading ? (
              <Loader className="mx-auto animate-spin" size={16} />
            ) : (
              'Add Savings'
            )}
          </button>
        </div>
      </form>

      {isModalSuccessOpen && (
        <AuthAnimatedModal
          isOpen={isModalSuccessOpen}
          onClose={handleProceed}
          onConfirm={handleProceed}
          title="Savings Added Successfully!"
          btnText="Proceed"
          content={
            <p>
              You have successfully added{' '}
              <span className="font-semibold">
                {formatToNaira(Number(formik.values.amount))}
              </span>{' '}
              to your savings. Thank you!
            </p>
          }
        />
      )}
    </Modal>
  );
}

// Add Paystack to the global window object
declare global {
  interface Window {
    PaystackPop: any;
  }
}

export default AddSavingsModal;
